/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { BUTTON_FORMAT_SOFT, BUTTON_THEME_DEFAULT, Button, SPINNER_TYPE_REGULAR, Spinner } from '@squiz/sds';
import React, { useCallback, useEffect, useState } from 'react';
import { lastValueFrom } from 'rxjs';
import { COMPONENT_SERVICE_STRINGS } from '../../../../Constants/Strings.constants';
import {
  ComponentServiceComponentSetDto,
  EntityComponentSet,
  EntityComponentSetItem,
  getComponentService,
} from '../../../../Services/component';
import { COMPONENT_SETS_DATA_IDS } from '../../ComponentSets.constants';
import { ComponentSetItem } from '../ComponentSetItem/ComponentSetItem';
import { ComponentSetListEmptyState } from '../ComponentSetListEmptyState/ComponentSetListEmptyState';

/**
 * Component set list.
 */
export const ComponentSetList = (): JSX.Element => {
  const [page, setPage] = useState<number>(1);
  const [nextPage, setNextPage] = useState<boolean>(true);
  const [componentSetData, setComponentSetData] = useState<Array<EntityComponentSetItem>>([]);
  const [isLoading, setLoading] = useState(true);

  // Component service
  const componentService = getComponentService();

  const componentSetListFooter: JSX.Element = (
    <div className="component-sets__list__show-more">
      <Button
        data-id={COMPONENT_SETS_DATA_IDS.showMore}
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => setPage(page + 1)}
        text={COMPONENT_SERVICE_STRINGS.loadMore}
        theme={BUTTON_THEME_DEFAULT}
      />
    </div>
  );

  const fetchComponentSets = useCallback((): void => {
    // Sort alphabetically by displayName
    const sortBy = 'displayName';

    // DTO to use when requesting component sets
    const dto: ComponentServiceComponentSetDto = { page, sortBy };

    if (nextPage) {
      // Send request to get latest component sets with current page
      lastValueFrom(componentService.getComponentSet(dto))
        .then((res) => {
          // At this point, we know a tenant exists so we will get a non-null value
          const response = res as EntityComponentSet;

          // Check if there is another page after this one
          if (!response.pageInfo.next) {
            setNextPage(false);
          }

          // Update the component sets with updated data
          setLoading(false);
          return setComponentSetData([...componentSetData, ...response.items]);
        })
        .catch(() => {
          // Do nothing for now
        });
    }
    // We don't want to have componentSetData as a dependency, disabled eslint
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentService, isLoading, page, setLoading]);

  useEffect(() => {
    // Fetch component sets when component is mounted & when page changes
    fetchComponentSets();
  }, [fetchComponentSets, page]);

  const Loader = (): JSX.Element => {
    return <div className="loading">{<Spinner type={SPINNER_TYPE_REGULAR} />}</div>;
  };

  const componentSetListDetailed = (): JSX.Element => {
    const componentItems: Array<JSX.Element> = componentSetData.map((set: EntityComponentSetItem, index: number) => {
      return <ComponentSetItem key={index} set={set} />;
    });
    // Render the items in the list
    return (
      <>
        <div className="component-sets__list" data-id={COMPONENT_SETS_DATA_IDS.list}>
          {componentItems}
        </div>
        {/* Show loading component if next page is being requested */}
        {nextPage && componentSetListFooter}
      </>
    );
  };

  const componentSetListEmpty = (): JSX.Element => {
    return (
      <div className="component-sets__list-container --empty" data-testid={COMPONENT_SETS_DATA_IDS.listEmptyState}>
        <ComponentSetListEmptyState />
      </div>
    );
  };

  if (isLoading) {
    return Loader();
  }

  if (componentSetData.length === 0) {
    return componentSetListEmpty();
  }

  return componentSetListDetailed();
};
